import { defineStore } from 'pinia';
import { normalizeAnimeTitlePreference } from '../utils/animePaths';
import { normalizeRoutePath } from '../utils/pagination';

const SETTINGS_STORAGE_KEY = 'bookmark-preferences';
const LEGACY_THEME_STORAGE_KEY = 'bookmark-theme';

const DEFAULT_PREFERENCES = Object.freeze({
    animeTitlePreference: 'english',
    theme: 'dark',
    postLoginLanding: 'current-season',
    overviewTab: 'details',
    episodeGuideDefaultFilter: 'all',
    releaseTimeDisplayMode: 'local',
    spoilerSafeMode: false
});

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

export const normalizePostLoginLandingPreference = (value = '') => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (['home', 'current-season', 'bookmarks', 'releases', 'news', 'discover', 'archive', 'watch-order'].includes(normalized)) {
        return normalized;
    }
    return DEFAULT_PREFERENCES.postLoginLanding;
};

export const normalizeOverviewTabPreference = (value = '') => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (['details', 'episodes', 'characters', 'staff', 'watch-order'].includes(normalized)) {
        return normalized;
    }
    return DEFAULT_PREFERENCES.overviewTab;
};

export const normalizeEpisodeGuideDefaultFilter = (value = '') => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (['all', 'canon', 'hide-recap', 'hide-filler'].includes(normalized)) {
        return normalized;
    }
    return DEFAULT_PREFERENCES.episodeGuideDefaultFilter;
};

export const normalizeReleaseTimeDisplayMode = (value = '') => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    if (['local', 'source'].includes(normalized)) {
        return normalized;
    }
    return DEFAULT_PREFERENCES.releaseTimeDisplayMode;
};

export const normalizeThemePreference = (value = '') => {
    const normalized = `${value || ''}`.trim().toLowerCase();
    return normalized === 'light' ? 'light' : 'dark';
};

export const normalizeSpoilerSafeMode = (value = false) => {
    if (typeof value === 'string') {
        return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
    }
    return Boolean(value);
};

const normalizeStoredPreferences = (value = {}) => ({
    animeTitlePreference: normalizeAnimeTitlePreference(value?.animeTitlePreference),
    theme: normalizeThemePreference(value?.theme),
    postLoginLanding: normalizePostLoginLandingPreference(value?.postLoginLanding),
    overviewTab: normalizeOverviewTabPreference(value?.overviewTab),
    episodeGuideDefaultFilter: normalizeEpisodeGuideDefaultFilter(value?.episodeGuideDefaultFilter),
    releaseTimeDisplayMode: normalizeReleaseTimeDisplayMode(value?.releaseTimeDisplayMode),
    spoilerSafeMode: normalizeSpoilerSafeMode(value?.spoilerSafeMode)
});

const getCurrentSeasonKey = (date = new Date()) => {
    const month = date.getMonth();
    if (month >= 3 && month <= 5) {
        return 'spring';
    }
    if (month >= 6 && month <= 8) {
        return 'summer';
    }
    if (month >= 9 && month <= 11) {
        return 'fall';
    }
    return 'winter';
};

export const getCurrentSeasonPath = (date = new Date()) => {
    return normalizeRoutePath(`/seasons/${getCurrentSeasonKey(date)}/${date.getFullYear()}`);
};

export const getPostLoginLandingPath = (preference = DEFAULT_PREFERENCES.postLoginLanding, date = new Date()) => {
    const normalizedPreference = normalizePostLoginLandingPreference(preference);
    if (normalizedPreference === 'home') {
        return '/';
    }
    if (['bookmarks', 'releases', 'news', 'discover', 'archive', 'watch-order'].includes(normalizedPreference)) {
        return normalizeRoutePath(`/${normalizedPreference}`);
    }
    return getCurrentSeasonPath(date);
};

const readLegacyThemePreference = () => {
    if (!canUseStorage()) {
        return DEFAULT_PREFERENCES.theme;
    }

    return normalizeThemePreference(window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY) || DEFAULT_PREFERENCES.theme);
};

export const readStoredPreferences = () => {
    if (!canUseStorage()) {
        return {
            ...DEFAULT_PREFERENCES
        };
    }

    const rawValue = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!rawValue) {
        return {
            ...DEFAULT_PREFERENCES,
            theme: readLegacyThemePreference()
        };
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        const retiredKeys = ['bookmarksLayoutStyle', 'bookmarksLayoutPreferenceVersion'];
        if (parsedValue && retiredKeys.some(key => Object.prototype.hasOwnProperty.call(parsedValue, key))) {
            const currentValues = { ...parsedValue };
            retiredKeys.forEach(key => { delete currentValues[key]; });
            // Clean up the retired layout only; preserve every other saved value.
            // Restricted storage must not turn a valid read into default settings.
            try { window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(currentValues)); } catch (_error) { /* Read-only storage is still usable. */ }
        }
        return {
            ...DEFAULT_PREFERENCES,
            ...normalizeStoredPreferences(parsedValue),
            theme: parsedValue?.theme ? normalizeThemePreference(parsedValue.theme) : readLegacyThemePreference()
        };
    } catch (_error) {
        return {
            ...DEFAULT_PREFERENCES,
            theme: readLegacyThemePreference()
        };
    }
};

const persistPreferences = (value = {}) => {
    if (!canUseStorage()) {
        return;
    }

    const normalizedValue = {
        ...DEFAULT_PREFERENCES,
        ...normalizeStoredPreferences(value)
    };

    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(normalizedValue));
    window.localStorage.setItem(LEGACY_THEME_STORAGE_KEY, normalizedValue.theme);
};

export const usePreferences = defineStore('preferences', {
    state: () => ({
        ...readStoredPreferences()
    }),
    actions: {
        hydrateFromStorage() {
            Object.assign(this, readStoredPreferences());
        },
        setAnimeTitlePreference(value = '') {
            this.animeTitlePreference = normalizeAnimeTitlePreference(value);
            persistPreferences(this.$state);
        },
        setThemePreference(value = '') {
            this.theme = normalizeThemePreference(value);
            persistPreferences(this.$state);
        },
        setPostLoginLanding(value = '') {
            this.postLoginLanding = normalizePostLoginLandingPreference(value);
            persistPreferences(this.$state);
        },
        setOverviewTab(value = '') {
            this.overviewTab = normalizeOverviewTabPreference(value);
            persistPreferences(this.$state);
        },
        setEpisodeGuideDefaultFilter(value = '') {
            this.episodeGuideDefaultFilter = normalizeEpisodeGuideDefaultFilter(value);
            persistPreferences(this.$state);
        },
        setReleaseTimeDisplayMode(value = '') {
            this.releaseTimeDisplayMode = normalizeReleaseTimeDisplayMode(value);
            persistPreferences(this.$state);
        },
        setSpoilerSafeMode(value = false) {
            this.spoilerSafeMode = normalizeSpoilerSafeMode(value);
            persistPreferences(this.$state);
        }
    }
});
